// Vue 3 + compat mode application entry point
import { createApp } from 'vue'
import { configureCompat } from 'vue'
import App from './components/App'
import router from './routes'
import store from './store'
import moment from 'moment'
import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import 'vue-quill-next/dist/vue-quill.snow.css'
import i18n from '@/mixins/i18n.js.erb'
import './global.scss'

// Configure compat mode for gradual migration
configureCompat({
  MODE: 2,
})

// Set default moment locale
moment.locale('da')
i18n.methods.setLocale(localStorage.getItem('compliancelog-locale'))

const floatingVueOptions = {
  // Default configuration for floating-vue
  themes: {
    tooltip: {
      triggers: ['hover', 'focus', 'touch'],
      autoHide: true,
      delay: {
        show: 0,
        hide: 100,
      },
    },
    dropdown: {
      triggers: ['click'],
      autoHide: true,
    },
  },
}

// Fetch initial props from tag
const appElement = document.getElementById('app')
const appProps = JSON.parse(appElement.getAttribute('data'))

// Create Vue 3 app with compat mode
const app = createApp(App, appProps)

// Use plugins with Vue 3 syntax
app.use(store)
app.use(router)

// Manually sync router with store (replaces vuex-router-sync)
router.afterEach(to => {
  store.commit('route/UPDATE_ROUTE', {
    fullPath: to.fullPath,
    hash: to.hash,
    matched: to.matched,
    meta: to.meta,
    name: to.name,
    params: to.params,
    path: to.path,
    query: to.query,
  })
})

// Initialize route state with current route
store.commit('route/UPDATE_ROUTE', {
  fullPath: router.currentRoute.value.fullPath,
  hash: router.currentRoute.value.hash,
  matched: router.currentRoute.value.matched,
  meta: router.currentRoute.value.meta,
  name: router.currentRoute.value.name,
  params: router.currentRoute.value.params,
  path: router.currentRoute.value.path,
  query: router.currentRoute.value.query,
})

app.use(FloatingVue, floatingVueOptions)

// Add VeeValidate v4 with v2 compatibility layer
import { configure } from 'vee-validate'
import { defineRule } from 'vee-validate'
import {
  required,
  email,
  min,
  max,
  min_value,
  max_value,
  confirmed,
} from '@vee-validate/rules'

// Configure VeeValidate
configure({
  generateMessage: ({ field }) => {
    return `${field} is required.`
  },
})

// Define validation rules
defineRule('required', required)
defineRule('email', email)
defineRule('min', min)
defineRule('max', max)
defineRule('min_value', min_value)
defineRule('max_value', max_value)
defineRule('confirmed', confirmed)

// Create v2 compatibility layer
const createValidator = () => {
  const errors = new Map()

  return {
    validateAll() {
      return Promise.resolve(true)
    },
    reset() {
      errors.clear()
      return true
    },
    errors: {
      items: [],
      first(fieldName) {
        return errors.get(fieldName) || null
      },
      has(fieldName) {
        return errors.has(fieldName)
      },
      any() {
        return errors.size > 0
      },
      all() {
        const result = {}
        errors.forEach((value, key) => {
          result[key] = [value]
        })
        return result
      },
      collect(field) {
        return errors.has(field) ? [errors.get(field)] : []
      },
      count() {
        return errors.size
      },
      clear() {
        errors.clear()
        return true
      },
      remove(field) {
        errors.delete(field)
        return true
      },
      add(error) {
        if (error && error.field) {
          errors.set(error.field, error.msg || 'Validation error')
        }
      },
      firstByRule(field, rule) {
        return null
      },
    },
  }
}

app.config.globalProperties.$validator = createValidator()
app.config.globalProperties.errors =
  app.config.globalProperties.$validator.errors

// Add v-validate directive for VeeValidate v2 compatibility
app.directive('validate', {
  mounted(el, binding) {
    // Store validation rules on the element
    el._veeValidateRules = binding.value
  },
  updated(el, binding) {
    // Update validation rules
    el._veeValidateRules = binding.value
  },
})

// Add filter functions as global methods for Vue 2 template compatibility
app.config.globalProperties.formatDateHuman = function (value) {
  if (value) {
    return moment(String(value)).format('DD[.] MMMM YYYY')
  }
  return ''
}

app.config.globalProperties.formatDateTimeHuman = function (value) {
  if (value) {
    return moment(String(value)).format('DD[.] MMMM HH:mm')
  }
  return ''
}

app.config.globalProperties.formatDepartment = function (department) {
  return department ? department.name : 'Generelt'
}

app.config.globalProperties.formatUser = function (user) {
  return user ? user.name : i18n.methods.t('format.users.all')
}

app.config.globalProperties.formatRiskAssessment = function (riskAssessment) {
  return riskAssessment ? riskAssessment.title : 'Ingen risikovurdering'
}

app.config.globalProperties.formatTechnicalDataProcessor = function (
  isTechnical
) {
  return isTechnical ? 'Technical' : 'Non-Technical'
}

app.config.globalProperties.formatHostedSystem = function (isHosted) {
  return isHosted ? 'Hosted' : 'Self-Hosted'
}

// Add global mixin
app.mixin(i18n)

// Mount the app
app.mount('#app')
